import React, {Component} from 'react'
import RemotingService from "../../../../../services/remoting-service/RemotingService";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import moment from "moment";
import {Button, ButtonGroup} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Link} from "react-router-dom";
import PrimeDateRangeInput from "../../../../../components/date-input/PrimeDateRangeInput";
import {TextInput} from "../../../../../components";
import DateUtil from "../../../../../utils/DateUtil";
import confirmDialog from "../../../../../components/dialog/ConfirmDialog";
import NotificationService from "../../../../../services/notification-service/NotificationService";
import AuthService from "../../../../../services/auth-service/AuthService";
import dialog from "../../../../../components/dialog/Dialog";
import EntityNoteListModal from "../notes/EntityNoteListModal";

// No claim yet -> Pending or (manually) Audited; once the claim exists this tab's work is done (Claim Generated)
const statusLabel = item => {
    if (item.status === 'AUDITED') {
        return 'Audited';
    }
    return item.status === 'CLAIM_GENERATED' ? 'Claim Generated' : 'Pending';
};
const formatMoney = value => value != null ? Number(value).toFixed(2) : '';
const moneyStyle = {textAlign: 'right'};

export default class UnclaimedInvoiceList extends Component {

    constructor(props) {
        super(props);
        this.state = {
            items: [],
            selectedItems: [],
            dateRange: [new Date(), new Date()],
            billType: 'DIRECT_BILLING',
            patientEid: '',
            patientMrn: '',
            invoiceId: '',
            generating: false
        };
    }

    componentDidMount() {
        this.retrieveItems();
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.selected && this.props.selected) {
            this.retrieveItems();
        }
    }

    handleDateRangeChange = (dateRange) => {
        this.setState({dateRange}, () => {
            // Only fetch when both start and end dates are selected
            if (dateRange && dateRange[0] && dateRange[1]) {
                this.retrieveItems();
            }
        });
    }

    handleBillTypeChange = (billType) => {
        if (billType === this.state.billType) {
            return;
        }
        this.setState({billType, selectedItems: []}, this.retrieveItems);
    }

    retrieveItems = () => {
        const [fromDate, toDate] = this.state.dateRange || [];
        if (!fromDate || !toDate) {
            return;
        }

        const params = {
            fromDate: moment(fromDate).format('YYYY-MM-DD'),
            toDate: moment(toDate).format('YYYY-MM-DD'),
            billType: this.state.billType
        };
        if (this.state.patientEid.trim()) {
            params.patientEid = this.state.patientEid.trim();
        }
        if (this.state.patientMrn.trim()) {
            params.patientMrn = this.state.patientMrn.trim();
        }
        if (this.state.invoiceId.trim()) {
            params.invoiceId = this.state.invoiceId.trim();
        }

        RemotingService.getRemoteCall(
            'api/insurance/coordinator/v2/claims/unclaimed-invoices',
            params,
            (items) => {
                const withLabels = items.map(item => ({...item, statusLabel: statusLabel(item)}));
                this.setState({items: withLabels, selectedItems: []});
            });
    }

    // Rows that already have a claim stay visible for their status but cannot be selected again
    handleSelectionChange = (selected) => {
        this.setState({selectedItems: selected.filter(item => item.status !== 'CLAIM_GENERATED')});
    }

    // Marks the selected Pending invoices as Audited, or takes the mark back from the selected Audited ones
    updateAudit = (audited) => {
        const fromStatus = audited ? 'PENDING' : 'AUDITED';
        const invoiceIds = this.state.selectedItems
            .filter(item => item.status === fromStatus)
            .map(item => item.invoiceId);
        RemotingService.postRemoteCall('api/insurance/coordinator/v2/claims/unclaimed-invoices/audit',
            {invoiceIds, audited},
            () => this.retrieveItems(),
            () => {});
    }

    // Notes of this tab only; the bill and invoice item notes are separate
    openNotesModal = (item) => {
        dialog({
            title: "Invoice Notes",
            component: <EntityNoteListModal entityId={item.invoiceId}
                                            baseUrl={"api/insurance/coordinator/v2/claims/invoice"}
                                            readOnly={!AuthService.userHasAuthority("UPDATE_INSURANCE_CLAIM")}
                                            onNotesChange={() => this.refreshNotes(item.invoiceId)}/>,
            size: "lg"
        });
    }

    // Updates one row's notes without reloading the list, so the selection survives
    refreshNotes = (invoiceId) => {
        RemotingService.getRemoteCall(`api/insurance/coordinator/v2/claims/invoice/${invoiceId}/notes`,
            null,
            (notes) => this.setState(prev => ({
                items: prev.items.map(item => item.invoiceId === invoiceId ? {...item, notes: notes || []} : item)
            })));
    }

    notesTemplate = (item) => {
        const notes = item.notes || [];
        const notesCount = notes.filter(note => note.isActive !== false).length;
        return <Button variant={"link"} style={{color: "#007bff", fontSize: "14px"}}
                       onClick={() => this.openNotesModal(item)}>
            <FontAwesomeIcon icon={["fas", "sticky-note"]} size={"sm"}/> Notes ({notesCount})
        </Button>;
    }

    statusTemplate = (item) => {
        if (item.status !== 'AUDITED') {
            return item.statusLabel;
        }
        const auditedAt = item.auditedDate ? DateUtil.formatDateTime12H(item.auditedDate) : '';
        return <span title={[item.auditedBy, auditedAt].filter(Boolean).join(' - ')}>{item.statusLabel}</span>;
    }

    generateClaims = () => {
        const selectedInvoiceIds = this.state.selectedItems.map(item => item.invoiceId);
        confirmDialog("Generate Claims",
            `Generate claims for ${selectedInvoiceIds.length} selected invoice(s)?`,
            () => {
                this.setState({generating: true});
                RemotingService.postRemoteCall('api/insurance/coordinator/v2/claims/generate-from-invoices',
                    selectedInvoiceIds,
                    (result) => {
                        this.setState({generating: false});
                        if (result.generated.length > 0) {
                            NotificationService.showNotification({
                                severity: 'success',
                                summary: 'Claims Generated',
                                detail: `${result.generated.length} claim(s) generated. Continue in the Claim tab.`
                            });
                        }
                        if (result.skipped.length > 0) {
                            NotificationService.showNotification({
                                severity: 'warn',
                                summary: 'Skipped Invoices',
                                detail: result.skipped.map(s => `Invoice ${s.invoiceId}: ${s.reason}`).join('\n')
                            });
                        }
                        if (result.warnings.length > 0) {
                            NotificationService.showNotification({
                                severity: 'warn',
                                summary: 'Warnings',
                                detail: result.warnings.join('\n')
                            });
                        }
                        this.retrieveItems();
                    }, () => {
                        this.setState({generating: false});
                    });
            });
    }

    isOnlyAuditedSelected = () => {
        const selected = this.state.selectedItems;
        return selected.length > 0 && selected.every(item => item.status === 'AUDITED');
    }

    renderColumns() {
        const columns = [
            <Column key="select" selectionMode="multiple" headerStyle={{width: '3em'}}/>,
            <Column key="invoiceId" header="Invoice No" field="invoiceId" filter filterMatchMode="contains"
                    body={item => AuthService.userHasAuthority("VIEW_BILLING_INVOICE")
                        ? <Link to={{pathname: '/billing', invoiceId: item.invoiceId, invoiceDate: item.invoiceDate}}>{item.invoiceId}</Link>
                        : item.invoiceId}/>,
            <Column key="creationDate" header="Date" field="creationDate"
                    body={item => item.creationDate ? DateUtil.formatDateTime12H(item.creationDate) : ''}/>,
            <Column key="patientName" header="Patient" field="patientName" filter filterMatchMode="contains"/>,
            <Column key="patientEid" header="Patient EID" field="patientEid" filter filterMatchMode="contains"/>,
            <Column key="patientMrn" header="Patient MRN" field="patientMrn" filter filterMatchMode="contains"/>,
            <Column key="modality" header="Modality" field="modality" filter filterMatchMode="contains"/>
        ];
        if (this.state.billType === 'DIRECT_BILLING') {
            columns.push(<Column key="insuranceName" header="Insurance" field="insuranceName" filter filterMatchMode="contains"/>);
        }
        columns.push(
            <Column key="gross" header="Gross" field="gross" body={item => formatMoney(item.gross)} bodyStyle={moneyStyle}/>,
            <Column key="net" header="Net" field="net" body={item => formatMoney(item.net)} bodyStyle={moneyStyle}/>,
            <Column key="credit" header="Credit" field="credit" body={item => formatMoney(item.credit)} bodyStyle={moneyStyle}/>,
            <Column key="patientPayable" header="Patient Payable" field="patientPayable"
                    body={item => formatMoney(item.patientPayable)} bodyStyle={moneyStyle}/>,
            <Column key="approvalCode" header="Approval Code" field="insuranceApprovalCode" filter filterMatchMode="contains"/>,
            <Column key="notes" header="Note" body={this.notesTemplate}/>,
            <Column key="status" header="Status" field="statusLabel" filter filterMatchMode="contains"
                    body={this.statusTemplate}/>
        );
        return columns;
    }

    render() {
        return (
            <>
                <div className="float-left" style={{display: "flex", gap: "10px"}}>
                    <PrimeDateRangeInput value={this.state.dateRange}
                                         onChange={this.handleDateRangeChange}/>
                    <ButtonGroup style={{height: "fit-content"}}>
                        <Button variant="outline-secondary"
                                active={this.state.billType === 'DIRECT_BILLING'}
                                onClick={() => this.handleBillTypeChange('DIRECT_BILLING')}>
                            Direct Billing
                        </Button>
                        <Button variant="outline-secondary"
                                active={this.state.billType === 'SELF_PAY'}
                                onClick={() => this.handleBillTypeChange('SELF_PAY')}>
                            Self-Pay
                        </Button>
                    </ButtonGroup>
                    <TextInput placeholder="Patient EID" value={this.state.patientEid}
                               onChange={value => this.setState({patientEid: value})}
                               onKeyDown={e => e.key === 'Enter' && this.retrieveItems()}/>
                    <TextInput placeholder="Patient MRN" value={this.state.patientMrn}
                               onChange={value => this.setState({patientMrn: value})}
                               onKeyDown={e => e.key === 'Enter' && this.retrieveItems()}/>
                    <TextInput placeholder="Invoice No" value={this.state.invoiceId}
                               onChange={value => this.setState({invoiceId: value})}
                               onKeyDown={e => e.key === 'Enter' && this.retrieveItems()}/>
                    <Button variant="light" onClick={this.retrieveItems}>
                        <FontAwesomeIcon icon="search"/> Search
                    </Button>
                </div>
                <div className="float-right" style={{display: "flex", gap: "10px"}}>
                    {AuthService.userHasAuthority("UPDATE_INSURANCE_CLAIM") && this.isOnlyAuditedSelected() &&
                    <Button variant="light" onClick={() => this.updateAudit(false)}>
                        Unmark Audited
                    </Button>}
                    {AuthService.userHasAuthority("UPDATE_INSURANCE_CLAIM") &&
                    <Button variant="primary"
                            disabled={!this.state.selectedItems.some(item => item.status === 'PENDING')}
                            onClick={() => this.updateAudit(true)}>
                        Mark as Audited
                    </Button>}
                    {AuthService.userHasAuthority("UPDATE_INSURANCE_CLAIM") &&
                    <Button variant="success"
                            disabled={this.state.selectedItems.length === 0 || this.state.generating}
                            onClick={this.generateClaims}>
                        {this.state.generating ? 'Generating...' : 'Generate Claims'}
                    </Button>}
                </div>
                <div style={{padding: '10px'}} className="data-table-header-wrapper">
                    <div className="float-left">
                    </div>
                </div>
                <div style={{padding: '10px'}}>
                    <DataTable responsive
                               emptyMessage="No claimable invoices found"
                               value={this.state.items}
                               dataKey="invoiceId"
                               selection={this.state.selectedItems}
                               onSelectionChange={e => this.handleSelectionChange(e.value)}>
                        {this.renderColumns()}
                    </DataTable>
                </div>
            </>
        )
    }
}
